import { createApi } from '@reduxjs/toolkit/dist/query/react';
import { backendApiServiceUrl, createApiDefaults } from './base';
import * as yup from 'yup';
import { axiosBaseQueryWithAuth } from './axios';

// types copied from the backend project `nodejs`

export enum ConnectionStatus {
  CONNECTED = 'Connected',
  NOT_CONNECTED = 'Not Connected',
  AUTH_ERROR = 'Authentication Error',
}

const RequestTokenSchema = yup.object({
  redirect_uri: yup.string().required().trim(),
});

const RequestTokenResponseSchema = yup.object({
  // since there is no way to create a "union" schema in `yup` - https://github.com/jquense/yup/issues/593#issuecomment-605717756
  // one type, both fields should be included
  access_token: yup.string().optional().trim(),
  expiry_date: yup.number().optional().min(1),

  // another type
  requestAuthUrl: yup.string().optional().trim(),

  // always present
  status: yup.string().required().oneOf(Object.values(ConnectionStatus)),
});

type RequestToken = yup.InferType<typeof RequestTokenSchema>;
export type RequestTokenResponse = yup.InferType<typeof RequestTokenResponseSchema>;

const UpdateTokenSchema = yup.object({
  code: yup.string().required().trim(),
  redirect_uri: yup.string().required().trim(),
});
const UpdateTokenResponseSchema = yup.object({
  access_token: yup.string().required().trim(),
  expiry_date: yup.number().positive(),
  scope: yup.string().required(),
});

type UpdateToken = yup.InferType<typeof UpdateTokenSchema>;
export type UpdateTokenResponse = yup.InferType<typeof UpdateTokenResponseSchema>;

// end copied types

export const integrationsApi = createApi({
  ...createApiDefaults,
  refetchOnFocus: false,
  baseQuery: axiosBaseQueryWithAuth({ baseUrl: backendApiServiceUrl }),
  reducerPath: 'integrations', // this is a legacy name. if this is changed, everyone will be logged out
  tagTypes: ['integrations'],

  endpoints: (builder) => ({
    // TODO if we support another service, we can add another argument to this function and replace the `/gsc/` in the URL
    getIntegrationStatus: builder.query<RequestTokenResponse, string>({
      query: (schema) => {
        return {
          url: `integrations/gsc/${schema}`,
          method: 'GET',
          params: {
            redirect_uri: `${process.env.NEXT_PUBLIC_HOST_URL}/app/settings/connections`,
          },
        };
      },
      providesTags: ['integrations'],
    }),
    updateIntegrationToken: builder.mutation<UpdateTokenResponse, { schema: string; accessCode: string }>({
      query: ({ schema, accessCode }) => {
        return {
          url: `integrations/gsc/${schema}`,
          method: 'POST',
          data: {
            code: accessCode,
            redirect_uri: `${process.env.NEXT_PUBLIC_HOST_URL}/app/settings/connections`,
          },
        };
      },
      // TODO pessimistic updating was causing the incorrect status to appear in the UI
      // the backend was also missing the connection status in the response
      //
      // onQueryStarted: async ({ schema, accessCode }, { dispatch, queryFulfilled }) => {
      //   // pessimistic update
      //   // try {
      //   const { data: updateResult } = await queryFulfilled;
      //   const patchResult = dispatch(
      //     integrationsApi.util.updateQueryData('getIntegrationStatus', schema, (draft) => {
      //       // since redux uses shallow equality comparisons,
      //       // we should copy over the properties
      //       Object.assign(draft, updateResult);

      //       // we need to clear the old data too, otherwise the ui may show 'unconnected'
      //       draft.requestAuthUrl = undefined;

      //       // optimistically set the status
      //       draft.status = ConnectionStatus.CONNECTED;
      //     })
      //   );
      //   // } catch {
      //   //   // an error occurred executing the query, so force a refresh of the data
      //   //   integrationsApi.util.invalidateTags(['integrations']);
      //   // }
      // },
      invalidatesTags: ['integrations'],
    }),
    revokeIntegrationToken: builder.mutation<void, { schema: string }>({
      query: ({ schema }) => {
        return {
          url: `integrations/gsc/${schema}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: ['integrations'],
    }),
  }),
});

export const {
  useGetIntegrationStatusQuery,
  useLazyGetIntegrationStatusQuery,
  useUpdateIntegrationTokenMutation,
  useRevokeIntegrationTokenMutation,

  usePrefetch,
  middleware,
  reducer,
  endpoints,
  util,
  reducerPath,
} = integrationsApi;
export default integrationsApi;
